import React, { type PropsWithChildren, type JSX } from "react";
import { twMerge } from "tailwind-merge";

import { SpotBadge } from "./components/SpotBadge";
import { CountBadge } from "./components/CountBadge";
import { IconBadge } from "./components/IconBadge";

import type { CommonColors } from "../../../types/colors";

type BadgeVariant = "count" | "count-small" | "spot" | "spot-contained" | "icon" | "icon-small";

export interface BadgeProps {
  className?: string;
  variant?: BadgeVariant;
  color?: CommonColors;
  count?: number;
  icon?: JSX.Element;
  maxValue?: number;
  invisible?: boolean;
}

const BADGE_RENDERER: Record<BadgeVariant, (props: Omit<BadgeProps, "variant" | "children">) => JSX.Element> = {
  count: ({ className, color, count, maxValue, invisible }) => <CountBadge className={className} color={color} count={count} maxValue={maxValue} invisible={invisible} />,
  "count-small": ({ className, color, count, maxValue, invisible }) => (
    <CountBadge small className={className} color={color} count={count} maxValue={maxValue} invisible={invisible} />
  ),
  spot: ({ className, invisible, color }) => <SpotBadge className={className} invisible={invisible} color={color} />,
  "spot-contained": ({ className, invisible, color }) => <SpotBadge contained className={className} invisible={invisible} color={color} />,
  icon: ({ className, icon, color }) => <IconBadge className={className} icon={icon} color={color} />,
  "icon-small": ({ className, icon, color }) => <IconBadge small className={className} icon={icon} color={color} />,
};

export function SupBadge(props: PropsWithChildren<BadgeProps>) {
  const { variant = "count", className, children, ...restProps } = props;
  const hasChild = Boolean(children);
  const badgeProps = {
    ...restProps,
    className: twMerge(hasChild && "absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 text-neutral-700", className),
  };
  const badge = BADGE_RENDERER[variant](badgeProps);

  return !hasChild ? (
    badge
  ) : (
    <span className="relative inline-flex ">
      {children}
      {badge}
    </span>
  );
}
